// sse/SSEHub.ts
import type { DataphoneOperation } from '../../types/models/DataphoneOperation';

export type EventMap = {
    'inAppNotification:new': {
        reason: string;
        createdAt: string;
    };
    // Resultado del datáfono Sipay; solo lo recibe quien lanzó la venta
    'dataphoneOperation:updated': {
        tpv: { id: number };
        operation: DataphoneOperation;
    };
};

type AnyEvent = keyof EventMap;
type Handler<E extends AnyEvent> = (data: EventMap[E]) => void;

type ListenerMap = {
    [E in AnyEvent]: Set<Handler<E>>;
};

export class SSEHub {
    private es: EventSource | null = null;

    private listeners: ListenerMap = {
        'inAppNotification:new': new Set(),
        'dataphoneOperation:updated': new Set(),
    };

    private urlFactory: () => string;

    constructor(urlFactory: () => string) {
        this.urlFactory = urlFactory;
    }

    start() {
        if (this.es) return;
        const url = this.urlFactory();
        // withCredentials hace que envíe cookies si tu auth va por cookie
        this.es = new EventSource(url, { withCredentials: true });
        this.es.addEventListener('ready', () => {
            /* opcional */
        });
        this.es.onerror = () => {
            // El navegador reintenta solo (honra `retry:` del servidor)
            // Si necesitas lógica extra, cierras y vuelves a start()
        };

        // multiplexor genérico: registra un listener por cada eventName conocido
        (Object.keys(this.listeners) as AnyEvent[]).forEach((eventName) => {
            this.es!.addEventListener(eventName, (msg) =>
                this.dispatch(eventName, msg.data as string),
            );
        });
    }

    stop() {
        this.es?.close();
        this.es = null;
    }

    on<E extends AnyEvent>(event: E, handler: Handler<E>) {
        const set = this.listeners[event];
        if (set.size === 0) {
            // si el stream ya está abierto, enganchamos el listener de bajo nivel
            this.es?.addEventListener(event, (msg) =>
                this.dispatch(event, msg.data as string),
            );
        }
        set.add(handler);
        this.start(); // garantiza 1 conexión global
        return () => this.off(event, handler);
    }

    off<E extends AnyEvent>(event: E, handler: Handler<E>) {
        const set = this.listeners[event];
        set.delete(handler);
    }

    private dispatch<E extends AnyEvent>(event: E, raw: string) {
        const set = this.listeners[event];
        if (set.size === 0) return;
        let data: EventMap[E];
        try {
            data = JSON.parse(raw) as EventMap[E];
        } catch {
            return;
        }
        set.forEach((h) => h(data));
    }
}
